import React, { useEffect, useRef, useState } from 'react';
import { Target, Eye, Award, Shield, Users, Briefcase, CheckCircle, TrendingUp, Calendar, Lightbulb, Heart, Star, Zap } from 'lucide-react';
import { SEO } from '../components/SEO';

// Custom hook for intersection observer with scroll progress
const useScrollProgress = () => {
  const ref = useRef(null);
  const [progress, setProgress] = useState(0);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (!ref.current) return;
      
      const rect = ref.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const elementHeight = rect.height;
      
      // Calculate how much of the element has been scrolled through
      const scrolledPast = windowHeight - rect.top;
      const totalScrollDistance = windowHeight + elementHeight;
      const rawProgress = scrolledPast / totalScrollDistance;
      
      // Clamp between 0 and 1
      const clampedProgress = Math.min(Math.max(rawProgress, 0), 1);
      setProgress(clampedProgress);
      setIsInView(rect.top < windowHeight && rect.bottom > 0);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial check
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return [ref, progress, isInView];
};

// Custom hook for individual item visibility
const useItemInView = (options = {}) => {
  const ref = useRef(null);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsInView(true);
      }
    }, { threshold: 0.3, rootMargin: '-50px', ...options });

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, []);

  return [ref, isInView];
};

// Vertical Timeline Item Component
const VerticalTimelineItem = ({ milestone, index, isLeft }) => {
  const [ref, isInView] = useItemInView();
  
  return (
    <div 
      ref={ref}
      className={`relative flex items-center ${isLeft ? 'lg:flex-row' : 'lg:flex-row-reverse'} flex-col lg:gap-8`}
      style={{ minHeight: '120px' }}
    >
      {/* Card */}
      <div 
        className={`w-full lg:w-[calc(50%-40px)] transition-all duration-700 ease-out ${
          isInView 
            ? 'opacity-100 translate-x-0' 
            : `opacity-0 ${isLeft ? 'lg:-translate-x-12' : 'lg:translate-x-12'}`
        }`}
        style={{ transitionDelay: '200ms' }}
      >
        <div className={`bg-white rounded-xl p-6 shadow-lg border border-slate-200 hover:shadow-xl hover:border-teal-300 transition-all duration-300 ${isLeft ? 'lg:text-right' : 'lg:text-left'}`}>
          {/* Mobile year badge */}
          <div className="lg:hidden flex items-center gap-3 mb-3">
            <span className={`bg-teal-600 text-white px-4 py-2 rounded-full text-lg font-bold transition-all duration-500 ${
              isInView ? 'scale-100 opacity-100' : 'scale-75 opacity-0'
            }`}>
              {milestone.year}
            </span>
          </div>
          <h3 className="text-lg font-bold text-slate-800">{milestone.title}</h3>
        </div>
      </div>

      {/* Year Circle - Centered on the line */}
      <div 
        className={`hidden lg:flex absolute left-1/2 transform -translate-x-1/2 w-16 h-16 bg-teal-600 rounded-full items-center justify-center text-white font-bold text-lg shadow-lg z-20 border-4 border-white transition-all duration-500 ease-out ${
          isInView 
            ? 'scale-100 opacity-100' 
            : 'scale-0 opacity-0'
        }`}
      >
        {milestone.year}
      </div>

      {/* Spacer for the other side */}
      <div className="hidden lg:block w-[calc(50%-40px)]"></div>
    </div>
  );
};

// Animated Vertical Timeline Line Component
const AnimatedVerticalLine = ({ progress }) => {
  return (
    <div className="hidden lg:block absolute left-1/2 top-0 bottom-0 w-1 transform -translate-x-1/2">
      {/* Background line */}
      <div className="absolute inset-0 bg-slate-200 rounded-full"></div>
      {/* Animated growing line */}
      <div 
        className="absolute top-0 left-0 right-0 bg-gradient-to-b from-teal-400 via-teal-500 to-teal-600 rounded-full transition-all duration-100 ease-linear"
        style={{ height: `${progress * 100}%` }}
      ></div>
    </div>
  );
};

export const About = () => {
  const [timelineRef, scrollProgress] = useScrollProgress();
  
  const milestones = [
    { year: '2021', title: 'Company Founded' },
    { year: '2022', title: 'Sugar Plant Cogen Turnkey Project' },
    { year: '2023', title: 'Major Tyre Manufacturing Industry — 132Kv Switchyard and EHV 110 Kv Transmission Line' },
    { year: '2024', title: 'Supply, Erection, Testing and Commissioning of 220KV Transmission Line' },
    { year: '2025', title: 'Secured Turnkey Projects — 132Kv & 220Kv Bay (AVAADA)' }
  ];

  const values = [
    {
      icon: <Shield className="w-10 h-10" />,
      title: 'Safety First',
      description: 'Upholding the highest safety protocols and compliance standards in every project we undertake.'
    },
    {
      icon: <Lightbulb className="w-10 h-10" />,
      title: 'Innovation',
      description: 'Embracing new technologies and methodologies to deliver forward-thinking solutions.'
    },
    {
      icon: <Award className="w-10 h-10" />,
      title: 'Excellence',
      description: 'Setting benchmarks in quality and performance through rigorous standards.'
    },
    {
      icon: <Heart className="w-10 h-10" />,
      title: 'Sustainability',
      description: 'Prioritizing environmentally conscious practices in every project.'
    },
    {
      icon: <Users className="w-10 h-10" />,
      title: 'Customer Focus',
      description: 'Building lasting relationships through unparalleled service and trust.'
    }
  ];

  const capabilities = [
    'Turnkey execution with single-point responsibility',
    'Design, engineering, and procurement expertise',
    'Expert erection, testing, and commissioning',
    'Safety-first culture and quality-driven delivery',
    'Strong site coordination and project management',
    'Compliance-focused approach aligned with standards',
    'Multi-disciplinary engineering team',
    'Service coverage across multiple states'
  ];

  return (
    <div className="min-h-screen pt-28 pb-20">
      <SEO 
        title="About Us | Calspark Electrical & Engineers"
        description="Founded in 2021, Calspark Electrical & Engineers delivers turnkey electrical and instrumentation projects including switchyards, EHV transmission lines and 220KV systems across Pan India."
        keywords="about Calspark, EPC company Karnataka, electrical engineers Bidar Bengaluru, company history"
        ogUrl="/about"
      />
      
      {/* Hero Section */}
      <section className="relative h-[400px] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=1920&h=600&fit=crop)',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 to-slate-800/80"></div>
        </div>
        <div className="relative z-10 text-center text-white px-4">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">About Us</h1>
          <p className="text-xl text-slate-300 max-w-2xl mx-auto">
            Delivering Excellence in Electrical Infrastructure Since 2021
          </p>
        </div>
      </section>

      {/* Who We Are Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-slate-800 mb-8">Who We Are</h2>
              <div className="space-y-6 text-lg text-slate-600 leading-relaxed">
                <p>
                  <strong className="text-slate-800">Calspark Electrical & Engineers</strong> is a Government Licensed Electrical Contractor and a fast-growing EPC Electrical & Instrumentation contracting company, serving diversified segments of industry, co-operative sectors, and private corporate clients.
                </p>
                <p>
                  Established in 2021, we deliver dependable end-to-end project execution with a strong focus on safety, quality, compliance, and on-time delivery. We carry out turnkey jobs covering the complete lifecycle—design, engineering, procurement, supply, erection, testing, and commissioning—backed by a capable team of engineers, supervisors, and skilled & semi-skilled workforce.
                </p>
                <p>
                  Our mission is to create mutual value for customers by delivering a <strong className="text-teal-600">"Total Solution to Electrification & Controls"</strong>—with accountability, transparency, and a partnership-first approach.
                </p>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://customer-assets.emergentagent.com/job_ed6e21f6-4588-40b2-8743-fbb7a90d8f8d/artifacts/nv4ccfqs_Team.png"
                alt="Calspark Engineers reviewing project drawings"
                className="rounded-2xl shadow-2xl object-cover w-full"
              />
              {/* Stats overlay */}
              <div className="absolute -bottom-8 -left-8 bg-teal-600 text-white p-6 rounded-xl shadow-xl">
                <div className="flex items-center gap-6">
                  <div className="text-center">
                    <div className="text-4xl font-bold">5+</div>
                    <div className="text-sm text-teal-100">Years</div>
                  </div>
                  <div className="w-px h-12 bg-teal-400"></div>
                  <div className="text-center">
                    <div className="text-4xl font-bold">60+</div>
                    <div className="text-sm text-teal-100">Projects</div>
                  </div>
                  <div className="w-px h-12 bg-teal-400"></div>
                  <div className="text-center">
                    <div className="text-3xl font-bold">PAN</div>
                    <div className="text-sm text-teal-100">India</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Journey / Timeline Section - Vertical Layout */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-5xl mx-auto px-4 ml-auto mr-auto lg:pl-16">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-800 mb-4">Our Journey</h2>
            <p className="text-xl text-slate-600">The milestones that define our growth story</p>
          </div>
          
          {/* Vertical Timeline */}
          <div ref={timelineRef} className="relative py-8">
            {/* Animated vertical line */}
            <AnimatedVerticalLine progress={scrollProgress} />
            
            {/* Timeline items with proper spacing */}
            <div className="flex flex-col gap-12 lg:gap-16">
              {milestones.map((milestone, index) => {
                // Odd index (0, 2, 4) = years 2021, 2023, 2025 = LEFT side
                // Even index (1, 3) = years 2022, 2024 = RIGHT side
                const isLeft = index % 2 === 0;
                return (
                  <VerticalTimelineItem 
                    key={index}
                    milestone={milestone}
                    index={index}
                    isLeft={isLeft}
                  />
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Our Leadership Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-slate-800 mb-8">Our Leadership</h2>
              <p className="text-xl text-slate-600 leading-relaxed">
                At Calspark, leadership is built on the foundation of hands-on EPC experience, technical expertise, and an unwavering commitment to quality. Our leaders have navigated complex high-voltage projects, multi-state operations, and demanding timelines — bringing clarity, accountability, and vision to every project we undertake. Their drive to build India's power infrastructure, one project at a time, is what sets Calspark apart.
              </p>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=600&h=400&fit=crop"
                alt="Leadership"
                className="rounded-2xl shadow-xl w-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Our Team Section */}
      <section className="py-20 bg-gradient-to-br from-teal-600 to-teal-700 text-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <img
                src="https://images.unsplash.com/photo-1581092160562-40aa08e78837?w=600&h=400&fit=crop"
                alt="Our Team"
                className="rounded-2xl shadow-xl w-full object-cover"
              />
            </div>
            <div className="order-1 lg:order-2">
              <h2 className="text-4xl md:text-5xl font-bold mb-8">Our Team</h2>
              <p className="text-xl text-teal-100 leading-relaxed">
                At Calspark, our team is our strength. A group of highly skilled electrical engineers, project managers, and EPC professionals united by a shared passion for delivering reliable power infrastructure across India.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Vision Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl p-10 text-white">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 bg-teal-500/20 rounded-xl flex items-center justify-center">
                  <Target className="w-10 h-10 text-teal-400" />
                </div>
                <h3 className="text-3xl font-bold">Our Mission</h3>
              </div>
              <p className="text-lg text-slate-300 leading-relaxed">
                To create mutual value for customers by delivering a "Total Solution to Electrification & Controls" with accountability, transparency, and a partnership-first approach. We aim to be the most trusted EPC partner through excellence in execution and unwavering commitment to safety and quality.
              </p>
            </div>

            <div className="bg-gradient-to-br from-teal-600 to-teal-700 rounded-2xl p-10 text-white">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 bg-white/20 rounded-xl flex items-center justify-center">
                  <Eye className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-3xl font-bold">Our Vision</h3>
              </div>
              <p className="text-lg text-teal-100 leading-relaxed">
                To become a leading EPC contractor, recognized for delivering innovative and sustainable electrical infrastructure solutions. We aspire to set industry benchmarks in project execution, safety standards, and customer satisfaction while contributing to India's power sector growth.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section - Grid layout like Nanu */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-800 mb-4">Our Values</h2>
            <p className="text-xl text-slate-600">Principles that guide every decision and action we take</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            {values.map((value, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-8 hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border border-slate-200 text-center"
              >
                <div className="w-20 h-20 bg-teal-100 rounded-full flex items-center justify-center text-teal-600 mx-auto mb-6">
                  {value.icon}
                </div>
                <h3 className="text-xl font-bold text-slate-800 mb-3">{value.title}</h3>
                <p className="text-base text-slate-600 leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industry Insights Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-slate-800 mb-8">Industry Insights</h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-8">
                Electrical engineering services play a leading role in enabling 24×7 power supply and integrating smart grid technologies. With a consistent rise in power demand from across sectors and industries, the need for advanced industrial electrical systems continues to rise. With Calspark's expertise in high-voltage projects, EPC solutions, and turnkey execution, we are poised to meet the evolving demands of this rapidly advancing sector.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-lg">
                  <Zap className="w-6 h-6 text-teal-600 flex-shrink-0 mt-1" />
                  <p className="text-base text-slate-700">India is the third-largest producer and consumer of electricity worldwide, with an installed power capacity of 446+ GW.</p>
                </div>
                <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-lg">
                  <TrendingUp className="w-6 h-6 text-teal-600 flex-shrink-0 mt-1" />
                  <p className="text-base text-slate-700">The Indian power sector is projected to grow at a CAGR of 7.2%, with increasing demand for reliable power solutions.</p>
                </div>
                <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-lg">
                  <Star className="w-6 h-6 text-teal-600 flex-shrink-0 mt-1" />
                  <p className="text-base text-slate-700">India's power requirement is estimated to reach 817 GW by 2030 (CEA).</p>
                </div>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1509390288171-ce2088f7d08e?w=600&h=500&fit=crop"
                alt="Industry Insights"
                className="rounded-2xl shadow-xl w-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-800 mb-4">Why Calspark?</h2>
            <p className="text-xl text-slate-600">Your trusted partner for reliable EPC outcomes</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {capabilities.map((capability, index) => (
              <div
                key={index}
                className="flex items-start gap-3 p-5 bg-white rounded-lg hover:bg-teal-50 transition-colors border border-slate-200"
              >
                <CheckCircle className="w-6 h-6 text-teal-600 flex-shrink-0 mt-0.5" />
                <span className="text-base text-slate-700">{capability}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-slate-800 to-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Partner With Us
          </h2>
          <p className="text-xl text-slate-300 mb-8">
            Experience the Calspark difference in your next project
          </p>
          <a
            href="/contact"
            className="inline-block bg-teal-600 text-white px-10 py-4 rounded-lg text-lg font-medium hover:bg-teal-700 transition-all duration-300 shadow-lg"
          >
            Get in Touch
          </a>
        </div>
      </section>
    </div>
  );
};
